import type { ZoneCSVRow } from "./exportZones"
import type { ZoneType } from "../components/type"
import type { PlayerMetric } from "@/types/metric"

export type ImportPlayerMatch = {
  filePlayer: { name: string; uniformNumber: number }
  matchedPlayer: PlayerMetric | null
  matchType: "exact" | "name" | "number" | "manual" | "none"
  status: "matched" | "unmatched" | "skipped"
  csvRows: ZoneCSVRow[]
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ""
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === "," && !inQuotes) {
      result.push(current.trim())
      current = ""
    } else {
      current += char
    }
  }
  result.push(current.trim())
  return result
}

/**
 * CSV 문자열을 파싱하여 ZoneCSVRow 배열로 변환
 */
export function parseCSV(csvContent: string): {
  rows: ZoneCSVRow[]
  metricTypes: ZoneType[]
} {
  const content = csvContent.replace(/^\uFEFF/, "")
  const lines = content.trim().split(/\r?\n/)
  const metricTypes = new Set<ZoneType>()
  const rows: ZoneCSVRow[] = []

  if (lines.length < 2) {
    return { rows: [], metricTypes: [] }
  }

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i])
    if (values.length < 6) continue

    const name = values[0]?.replace(/"/g, "") ?? ""
    const uniformNumber = Number(values[1])
    const metricType = values[2] as ZoneType
    const active = values[3]?.toLowerCase() === "true"
    const customMetricActive = values[4]?.toLowerCase() === "true"
    const maxValue = values[5]
    const zoneValues = values.slice(6).filter((v) => v !== "")

    metricTypes.add(metricType)

    rows.push({
      playerName: name,
      uniformNumber,
      metricType,
      active,
      customMetricActive,
      maxValue,
      zoneValues: zoneValues.map((v) => (isNaN(Number(v)) ? v : Number(v))),
    })
  }

  return { rows, metricTypes: Array.from(metricTypes) }
}

/**
 * CSV에서 파싱한 선수를 기존 선수와 매칭
 * 매칭 우선순위: 1) 이름+등번호 완전 일치 → 2) 이름만 → 3) 등번호만
 */
export function matchPlayers(
  csvRows: ZoneCSVRow[],
  existingPlayers: PlayerMetric[],
): ImportPlayerMatch[] {
  const playerMap = new Map<string, ZoneCSVRow[]>()
  for (const row of csvRows) {
    const key = `${row.playerName}-${row.uniformNumber}`
    if (!playerMap.has(key)) {
      playerMap.set(key, [])
    }
    playerMap.get(key)!.push(row)
  }

  const matches: ImportPlayerMatch[] = []

  for (const [, rows] of playerMap) {
    const filePlayer = {
      name: rows[0].playerName,
      uniformNumber: rows[0].uniformNumber,
    }

    // 1. 이름 + 등번호 완전 일치
    const exactMatch = existingPlayers.find(
      (ep) => ep.name === filePlayer.name && ep.backNumber === filePlayer.uniformNumber,
    )
    if (exactMatch) {
      matches.push({
        filePlayer,
        matchedPlayer: exactMatch,
        matchType: "exact",
        status: "matched",
        csvRows: rows,
      })
      continue
    }

    // 2. 이름만 일치
    const nameMatch = existingPlayers.find((ep) => ep.name === filePlayer.name)
    if (nameMatch) {
      matches.push({
        filePlayer,
        matchedPlayer: nameMatch,
        matchType: "name",
        status: "matched",
        csvRows: rows,
      })
      continue
    }

    // 3. 등번호만 일치
    const numberMatch = existingPlayers.find((ep) => ep.backNumber === filePlayer.uniformNumber)
    if (numberMatch) {
      matches.push({
        filePlayer,
        matchedPlayer: numberMatch,
        matchType: "number",
        status: "matched",
        csvRows: rows,
      })
      continue
    }

    // 매칭 실패
    matches.push({
      filePlayer,
      matchedPlayer: null,
      matchType: "none",
      status: "unmatched",
      csvRows: rows,
    })
  }

  return matches
}
